"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ComponentAnatomy = void 0;
const lit_1 = require("lit");
const decorators_js_1 = require("lit/decorators.js");
const style_map_js_1 = require("lit/directives/style-map.js");
const component_styles_1 = require("@/components/domains/component/component.styles");
require("@/components/common");
require("@/components/indicators/list-marker/list-marker");
/**
 * 컴포넌트 해부도(Anatomy) 섹션.
 * 슬롯에 시연 대상을 넣고, code에 조립 마크업을 넘기면 제목 바로 아래에 코드 블록으로 보여줍니다.
 * parts 배열을 넘기면 번호 매긴 구성요소 범례를 덧붙이고, markers에 같은 순서로 위치를 넘기면
 * 무대 위에 그 번호를 찍습니다. 번호는 배열 순서에서 나오므로 범례와 어긋날 수 없습니다.
 * 나눌 구성요소가 없는 단일 요소 컴포넌트는 parts 없이 시연과 코드만 전시합니다.
 *
 * code의 바인딩 표현식 안에 `/`나 `>`가 들어가면 하이라이터가 여는 태그를 못 읽고 색을 잃으므로,
 * 데이터와 핸들러는 값을 펼치지 말고 이름으로 참조합니다.
 *
 * <mm-component-anatomy
 *   .parts=${["컨테이너", "옵션 버튼", "선택 인디케이터"]}
 *   .markers=${[{ placement: 'inline-start' }, { placement: 'block-end', offset: '25%' }]}
 *   .code=${'<mm-select> … </mm-select>'}
 * >
 *   < 시연 마크업 />
 * </mm-component-anatomy>
 */
let ComponentAnatomy = class ComponentAnatomy extends lit_1.LitElement {
    constructor() {
        super(...arguments);
        this.heading = 'Anatomy';
        this.parts = [];
        this.markers = [];
        this.code = '';
        this.fullWidth = false;
    }
    render() {
        return (0, lit_1.html) `
      <mm-heading level="2">${this.heading}</mm-heading>
      ${this.renderCode()}
      <div class="component-content-frame">
        <div class="measure">
          <div class="stage">
            <slot></slot>
            ${this.renderMarkers()}
          </div>
        </div>
      </div>
      ${this.renderParts()}
    `;
    }
    renderMarkers() {
        return this.markers.map((marker, index) => (0, lit_1.html) `
        <mm-list-marker
          variant="number"
          value=${index + 1}
          placement=${marker.placement}
          ?inset=${marker.inset}
          style=${(0, style_map_js_1.styleMap)({ '--component-anatomy-marker-offset': marker.offset ?? '50%' })}
        ></mm-list-marker>
      `);
    }
    renderParts() {
        if (!this.parts.length)
            return lit_1.nothing;
        return (0, lit_1.html) `
      <mm-text-list variant="number" .texts=${this.parts}></mm-text-list>
    `;
    }
    renderCode() {
        if (!this.code)
            return lit_1.nothing;
        return (0, lit_1.html) `
      <div class="component-content-frame">
        <div class="measure">
          <mm-code-block variant="plain" .code=${this.code} .copyable=${false}></mm-code-block>
        </div>
      </div>
    `;
    }
};
ComponentAnatomy.styles = [
    component_styles_1.componentContentFrameStyles,
    (0, lit_1.css) `
      :host {
        --component-anatomy-stage-width: auto;
        --component-anatomy-stage-padding: 0;

        display: flex;
        flex-direction: column;
        gap: var(--space-3);
        margin-top: var(--space-section);
      }

      /* 프레임은 가장자리까지 흘리고, 코드와 시연은 그 안에서 같은 폭의
         measure로 묶여 가운데로 모인다. 둘 다 measure의 시작점에서 뻗으므로
         폭이 달라도 시작점은 나란히 맞는다. */
      .measure {
        display: flex;
        flex-direction: column;
        align-items: start;
        max-width: var(--layout-width-small);
      }

      /* 시연 대상 없이 코드만 전시할 때는 빈 무대 프레임을 걷어낸다. */
      :host(:empty) .component-content-frame:has(.stage) {
        display: none;
      }

      /* 슬롯된 시연 대상과 번호 마커가 함께 앉는 무대. */
      .stage {
        width: var(--component-anatomy-stage-width);
        padding: var(--component-anatomy-stage-padding);
        position: relative;
      }

      /* 마커는 무대의 한 변을 기준으로 앉는다. 변 바깥으로 나가는 거리는
         마커 지름과 무대와의 간격의 합이고, inset이면 변 위로 들어온다. */
      mm-list-marker {
        --component-anatomy-marker-gutter: calc((var(--size-16) + var(--space-3)) * -1);

        position: absolute;
      }

      mm-list-marker[inset] {
        --component-anatomy-marker-gutter: 0px;
      }

      mm-list-marker[placement='inline-start'],
      mm-list-marker[placement='inline-end'] {
        top: var(--component-anatomy-marker-offset);
        transform: translateY(-50%);
      }

      mm-list-marker[placement='block-start'],
      mm-list-marker[placement='block-end'] {
        left: var(--component-anatomy-marker-offset);
        transform: translateX(-50%);
      }

      mm-list-marker[placement='inline-start'] {
        left: var(--component-anatomy-marker-gutter);
      }

      mm-list-marker[placement='inline-end'] {
        right: var(--component-anatomy-marker-gutter);
      }

      mm-list-marker[placement='block-start'] {
        top: var(--component-anatomy-marker-gutter);
      }

      mm-list-marker[placement='block-end'] {
        bottom: var(--component-anatomy-marker-gutter);
      }
    `,
];
__decorate([
    (0, decorators_js_1.property)({ type: String })
], ComponentAnatomy.prototype, "heading", void 0);
__decorate([
    (0, decorators_js_1.property)({ attribute: false })
], ComponentAnatomy.prototype, "parts", void 0);
__decorate([
    (0, decorators_js_1.property)({ attribute: false })
], ComponentAnatomy.prototype, "markers", void 0);
__decorate([
    (0, decorators_js_1.property)({ type: String })
], ComponentAnatomy.prototype, "code", void 0);
__decorate([
    (0, decorators_js_1.property)({ type: Boolean, attribute: 'full-width', reflect: true })
], ComponentAnatomy.prototype, "fullWidth", void 0);
ComponentAnatomy = __decorate([
    (0, decorators_js_1.customElement)('mm-component-anatomy')
], ComponentAnatomy);
exports.ComponentAnatomy = ComponentAnatomy;
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiY29tcG9uZW50LWFuYXRvbXkuanMiLCJzb3VyY2VSb290IjoiIiwic291cmNlcyI6WyJjb21wb25lbnQtYW5hdG9teS50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiOzs7Ozs7Ozs7QUFBQSw2QkFBb0Q7QUFDcEQscURBQTJEO0FBQzNELDhEQUFzRDtBQUV0RCxzRkFBNkY7QUFDN0YsK0JBQTRCO0FBQzVCLDJEQUF3RDtBQWF4RDs7Ozs7Ozs7Ozs7Ozs7Ozs7R0FpQkc7QUFFSSxJQUFNLGdCQUFnQixHQUF0QixNQUFNLGdCQUFpQixTQUFRLGdCQUFVO0lBQXpDOztRQTZFdUIsWUFBTyxHQUFHLFNBQVMsQ0FBQTtRQUNmLFVBQUssR0FBYSxFQUFFLENBQUE7UUFDcEIsWUFBTyxHQUF3QixFQUFFLENBQUE7UUFDckMsU0FBSSxHQUFHLEVBQUUsQ0FBQTtRQUNnQyxjQUFTLEdBQUcsS0FBSyxDQUFBO0lBbUR4RixDQUFDO0lBakRDLE1BQU07UUFDSixPQUFPLElBQUEsVUFBSSxFQUFBOzhCQUNlLElBQUksQ0FBQyxPQUFPO1FBQ2xDLElBQUksQ0FBQyxVQUFVLEVBQUU7Ozs7O2NBS1gsSUFBSSxDQUFDLGFBQWEsRUFBRTs7OztRQUkxQixJQUFJLENBQUMsV0FBVyxFQUFFO0tBQ3JCLENBQUE7SUFDSCxDQUFDO0lBRU8sYUFBYTtRQUNuQixPQUFPLElBQUksQ0FBQyxPQUFPLENBQUMsR0FBRyxDQUNyQixDQUFDLE1BQU0sRUFBRSxLQUFLLEVBQUUsRUFBRSxDQUFDLElBQUEsVUFBSSxFQUFBOzs7a0JBR1gsS0FBSyxHQUFHLENBQUM7c0JBQ0wsTUFBTSxDQUFDLFNBQVM7bUJBQ25CLE1BQU0sQ0FBQyxLQUFLO2tCQUNiLElBQUEsdUJBQVEsRUFBQyxFQUFFLG1DQUFtQyxFQUFFLE1BQU0sQ0FBQyxNQUFNLElBQUksS0FBSyxFQUFFLENBQUM7O09BRXBGLENBQ0YsQ0FBQTtJQUNILENBQUM7SUFFTyxXQUFXO1FBQ2pCLElBQUksQ0FBQyxJQUFJLENBQUMsS0FBSyxDQUFDLE1BQU07WUFBRSxPQUFPLGFBQU8sQ0FBQTtRQUV0QyxPQUFPLElBQUEsVUFBSSxFQUFBOzhDQUMrQixJQUFJLENBQUMsS0FBSztLQUNuRCxDQUFBO0lBQ0gsQ0FBQztJQUVPLFVBQVU7UUFDaEIsSUFBSSxDQUFDLElBQUksQ0FBQyxJQUFJO1lBQUUsT0FBTyxhQUFPLENBQUE7UUFFOUIsT0FBTyxJQUFBLFVBQUksRUFBQTs7O2lEQUdrQyxJQUFJLENBQUMsSUFBSSxjQUFjLEtBQUs7OztLQUd4RSxDQUFBO0lBQ0gsQ0FBQzs7QUFsSU0sdUJBQU0sR0FBRztJQUNkLDhDQUEyQjtJQUMzQixJQUFBLFNBQUcsRUFBQTs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7O0tBd0VGO0NBQ0YsQ0FBQTtBQUMyQjtJQUEzQixJQUFBLHdCQUFRLEVBQUMsRUFBRSxJQUFJLEVBQUUsTUFBTSxFQUFFLENBQUM7aURBQW9CO0FBQ2Y7SUFBL0IsSUFBQSx3QkFBUSxFQUFDLEVBQUUsU0FBUyxFQUFFLEtBQUssRUFBRSxDQUFDOytDQUFxQjtBQUNwQjtJQUEvQixJQUFBLHdCQUFRLEVBQUMsRUFBRSxTQUFTLEVBQUUsS0FBSyxFQUFFLENBQUM7aURBQWtDO0FBQ3JDO0lBQTNCLElBQUEsd0JBQVEsRUFBQyxFQUFFLElBQUksRUFBRSxNQUFNLEVBQUUsQ0FBQzs4Q0FBVTtBQUNnQztJQUFwRSxJQUFBLHdCQUFRLEVBQUMsRUFBRSxJQUFJLEVBQUUsT0FBTyxFQUFFLFNBQVMsRUFBRSxZQUFZLEVBQUUsT0FBTyxFQUFFLElBQUksRUFBRSxDQUFDO21EQUFrQjtBQWpGM0UsZ0JBQWdCO0lBRDVCLElBQUEsNkJBQWEsRUFBQyxzQkFBc0IsQ0FBQztHQUN6QixnQkFBZ0IsQ0FvSTVCO0FBcElZLDRDQUFnQiJ9